import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { type AdminRoaster, type RoasterStatus, roasterStatus } from '@/hooks/useRoasters';
import {
  ROASTER_PLATFORMS,
  displayHost,
  isPlatformCrawlable,
  platformLabel,
} from '@/lib/roasterPlatforms';

interface RoasterRowProps {
  roaster: AdminRoaster;
  // Returns an error message to show inline, or null on success.
  onSave: (id: string, input: { shopUrl: string; shopPlatformType: string }) => Promise<string | null>;
  // Marks the roaster as having no webshop at all. Same contract as onSave:
  // returns an error message to show inline, or null on success.
  onMarkNoWebshop: (id: string) => Promise<string | null>;
  // Queues a crawl for this roaster. Resolves once the run is accepted (or
  // refused) — the crawl itself runs off-request, so the row's spinner covers
  // the enqueue only. Absent when crawling is not offered.
  onRunCrawl?: (roaster: AdminRoaster) => Promise<void>;
}

function StatusBadge({ status }: { status: RoasterStatus }) {
  if (status === 'no-webshop') {
    return (
      <span
        className="flex shrink-0 items-center gap-1.5 whitespace-nowrap text-[11px] font-bold text-wolisho-brown/45"
        data-testid="roaster-status-no-webshop"
      >
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="9" />
          <line x1="5.6" y1="5.6" x2="18.4" y2="18.4" />
        </svg>
        No webshop
      </span>
    );
  }
  if (status === 'complete') {
    return (
      <span
        className="flex shrink-0 items-center gap-1 whitespace-nowrap text-[11px] font-bold text-wolisho-brown"
        data-testid="roaster-status-complete"
      >
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
        Complete
      </span>
    );
  }
  return (
    <span
      className="flex shrink-0 items-center gap-1.5 whitespace-nowrap text-[11px] font-bold text-wolisho-red"
      data-testid="roaster-status-needs-info"
    >
      <span className="h-[7px] w-[7px] rounded-full bg-wolisho-red" />
      Needs info
    </span>
  );
}

// Link to the staged-listing queue. Rendered wherever a roaster can hold crawl
// data — including a set-aside roaster, whose D1 listings outlive the flip.
function ReviewPill({ roaster }: { roaster: AdminRoaster }) {
  if (roaster.pendingListings <= 0) return null;
  return (
    <Link
      to={`/admin/roasters/${roaster.id}/review`}
      title={`${String(roaster.pendingListings)} staged listing${roaster.pendingListings === 1 ? '' : 's'} awaiting review`}
      data-testid="roaster-review"
      className="inline-flex items-center gap-1.5 rounded-lg bg-wolisho-brown/[0.09] px-3 py-1.5 text-[12.5px] font-bold text-wolisho-brown"
    >
      <span className="h-1.5 w-1.5 rounded-full bg-wolisho-brown" />
      Review {roaster.pendingListings}
    </Link>
  );
}

function PlatformChips({
  value,
  onChange,
}: {
  value: string | null;
  onChange: (value: string | null) => void;
}) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {ROASTER_PLATFORMS.map((platform) => {
        const active = value === platform.id;
        return (
          <button
            key={platform.id}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(active ? null : platform.id)}
            className={
              active
                ? 'rounded-lg border border-wolisho-brown bg-wolisho-brown px-3 py-1.5 text-xs font-bold text-white'
                : 'rounded-lg border border-wolisho-brown/[0.18] bg-transparent px-3 py-1.5 text-xs font-bold text-wolisho-brown'
            }
          >
            {platform.label}
          </button>
        );
      })}
    </div>
  );
}

export function RoasterRow({ roaster, onSave, onMarkNoWebshop, onRunCrawl }: RoasterRowProps) {
  const status = roasterStatus(roaster);
  const crawlable = status === 'complete' && isPlatformCrawlable(roaster.shopPlatformType);
  const [editing, setEditing] = useState(false);
  const [url, setUrl] = useState(roaster.shopUrl ? displayHost(roaster.shopUrl) : '');
  const [platform, setPlatform] = useState<string | null>(roaster.shopPlatformType);
  const [saving, setSaving] = useState(false);
  const [crawling, setCrawling] = useState(false);
  const [confirmingNoWebshop, setConfirmingNoWebshop] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const urlInputRef = useRef<HTMLInputElement>(null);

  // Marking a roaster no-webshop clears its shop URL and platform server-side
  // and there is no undo, so a row that has details to lose asks first.
  const noWebshopIsDestructive = Boolean(roaster.shopUrl) || Boolean(roaster.shopPlatformType);

  async function runCrawl() {
    if (crawling || !onRunCrawl) return;
    setCrawling(true);
    try {
      await onRunCrawl(roaster);
    } finally {
      setCrawling(false);
    }
  }

  const canSave = url.trim().length > 0 && platform !== null && !saving;

  // Focus the URL field when the row expands into edit mode.
  useEffect(() => {
    if (editing) urlInputRef.current?.focus();
  }, [editing]);

  function startEditing() {
    setUrl(roaster.shopUrl ? displayHost(roaster.shopUrl) : '');
    setPlatform(roaster.shopPlatformType);
    setError(null);
    setConfirmingNoWebshop(false);
    setEditing(true);
  }

  function cancel() {
    setEditing(false);
    setError(null);
    setConfirmingNoWebshop(false);
  }

  async function save() {
    const trimmedUrl = url.trim();
    if (saving || !trimmedUrl || platform === null) return;
    setSaving(true);
    setError(null);
    const message = await onSave(roaster.id, { shopUrl: trimmedUrl, shopPlatformType: platform });
    setSaving(false);
    if (message) {
      setError(message);
      return;
    }
    setEditing(false);
  }

  async function markNoWebshop() {
    if (saving) return;
    // First click on a row with shop details only arms the action; the PATCH
    // goes out on the confirming click.
    if (noWebshopIsDestructive && !confirmingNoWebshop) {
      setConfirmingNoWebshop(true);
      return;
    }
    setSaving(true);
    setError(null);
    const message = await onMarkNoWebshop(roaster.id);
    setSaving(false);
    setConfirmingNoWebshop(false);
    if (message) {
      setError(message);
      return;
    }
    setEditing(false);
  }

  return (
    <div className="px-4 py-3.5" data-testid={`roaster-row-${roaster.id}`}>
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <p className="truncate text-[15px] font-bold text-black">{roaster.name}</p>
          <p className="mt-px text-xs text-black/40">{roaster.location}</p>
        </div>
        <div className="pt-0.5">
          <StatusBadge status={status} />
        </div>
      </div>

      {editing ? (
        <div className="mt-3 flex flex-col gap-3">
          <div>
            <label
              htmlFor={`roaster-url-${roaster.id}`}
              className="mb-1.5 block text-[11px] font-bold text-wolisho-brown"
            >
              Shop URL
            </label>
            <input
              ref={urlInputRef}
              id={`roaster-url-${roaster.id}`}
              type="text"
              value={url}
              onChange={(event) => {
                setUrl(event.target.value);
                if (error) setError(null);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') void save();
              }}
              placeholder="e.g. roaster.com"
              className="w-full rounded-lg border border-wolisho-brown/20 bg-wolisho-cream px-3 py-2.5 text-sm text-black outline-hidden focus:border-wolisho-red"
            />
          </div>
          <div>
            <span className="mb-1.5 block text-[11px] font-bold text-wolisho-brown">
              Shop platform
            </span>
            <PlatformChips value={platform} onChange={setPlatform} />
          </div>
          {error && (
            <p className="text-xs font-bold text-wolisho-red" data-testid="roaster-row-error">
              {error}
            </p>
          )}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => void save()}
              disabled={!canSave}
              data-testid="roaster-save"
              className={
                canSave
                  ? 'flex min-w-[78px] items-center justify-center rounded-lg bg-wolisho-red px-[18px] py-2 text-[13px] font-bold text-white'
                  : 'flex min-w-[78px] items-center justify-center rounded-lg bg-wolisho-brown/[0.08] px-[18px] py-2 text-[13px] font-bold text-wolisho-brown/30'
              }
            >
              {saving ? (
                <span
                  className="h-[15px] w-[15px] animate-spin rounded-full border-2 border-white border-t-transparent"
                  aria-hidden="true"
                />
              ) : (
                'Save'
              )}
            </button>
            <button
              type="button"
              onClick={cancel}
              className="rounded-lg border border-wolisho-brown/20 px-3.5 py-2 text-[13px] font-bold text-wolisho-brown"
            >
              Cancel
            </button>
          </div>
          {/* On its own row, away from Save/Cancel: this discards data, so it
              must not sit where a reflowed "Cancel" would land. */}
          {status !== 'no-webshop' && (
            <div className="flex items-center gap-2 border-t border-wolisho-brown/[0.08] pt-3">
              <button
                type="button"
                onClick={() => void markNoWebshop()}
                disabled={saving}
                title={`${roaster.name} sells no coffee online`}
                data-testid="roaster-mark-no-webshop"
                className={
                  confirmingNoWebshop
                    ? 'shrink-0 rounded-lg border-[1.5px] border-wolisho-red px-3 py-1.5 text-[12.5px] font-bold text-wolisho-red disabled:opacity-50'
                    : 'shrink-0 text-[12.5px] font-bold text-wolisho-brown/50 underline underline-offset-2 disabled:opacity-50'
                }
              >
                {confirmingNoWebshop ? 'Discard shop details' : 'No webshop'}
              </button>
              {confirmingNoWebshop && (
                <>
                  <span
                    className="text-[12.5px] text-black/45"
                    data-testid="roaster-no-webshop-warning"
                  >
                    Clears the shop URL and platform — no undo.
                  </span>
                  <button
                    type="button"
                    onClick={() => setConfirmingNoWebshop(false)}
                    data-testid="roaster-no-webshop-cancel"
                    className="ml-auto shrink-0 text-[12.5px] font-bold text-wolisho-brown/50 underline underline-offset-2"
                  >
                    Keep
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      ) : status === 'no-webshop' ? (
        <div className="mt-2 flex items-center gap-2">
          <span className="text-[13px] text-black/35">Sells no coffee online</span>
          {/* Setting a roaster aside doesn't touch its crawl data, so any
              listings it already staged must stay reachable from here. */}
          <ReviewPill roaster={roaster} />
          <button
            type="button"
            onClick={startEditing}
            data-testid="roaster-add-info"
            className="ml-auto shrink-0 rounded-md border border-wolisho-brown/[0.15] px-2.5 py-1 text-xs font-bold text-wolisho-brown"
          >
            Add shop info
          </button>
        </div>
      ) : status === 'complete' && roaster.shopUrl ? (
        <div className="mt-2">
          <div className="flex min-w-0 items-center gap-2">
            <a
              href={roaster.shopUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex min-w-0 items-center gap-1.5 text-[13px] font-bold text-wolisho-red"
            >
              <span className="truncate">{displayHost(roaster.shopUrl)}</span>
              <svg
                width="11"
                height="11"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" />
                <polyline points="15 3 21 3 21 9" />
                <line x1="10" y1="14" x2="21" y2="3" />
              </svg>
            </a>
            <span className="text-black/20">·</span>
            <span className="shrink-0 text-[13px] font-bold text-black/55">
              {platformLabel(roaster.shopPlatformType)}
            </span>
          </div>
          <div className="mt-2.5 flex items-center gap-2">
            {crawlable ? (
              <button
                type="button"
                onClick={() => void runCrawl()}
                disabled={crawling}
                title={`Crawl ${roaster.name} now`}
                data-testid="roaster-run-crawl"
                className="inline-flex min-w-[104px] items-center justify-center gap-1.5 rounded-lg border-[1.5px] border-wolisho-red px-[13px] py-1.5 text-[12.5px] font-bold text-wolisho-red transition-colors hover:bg-wolisho-red hover:text-white disabled:cursor-not-allowed"
              >
                {crawling ? (
                  <span className="h-[13px] w-[13px] animate-spin rounded-full border-2 border-wolisho-red border-t-transparent" />
                ) : (
                  <>
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d="M6 4.5v15a1 1 0 001.53.85l12-7.5a1 1 0 000-1.7l-12-7.5A1 1 0 006 4.5z" />
                    </svg>
                    Run crawl
                  </>
                )}
              </button>
            ) : (
              <span
                title={`${platformLabel(roaster.shopPlatformType)} can’t be crawled automatically`}
                data-testid="roaster-not-crawlable"
                className="inline-flex items-center gap-1.5 rounded-lg bg-wolisho-brown/[0.05] px-[11px] py-1.5 text-xs font-bold text-wolisho-brown/40"
              >
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <circle cx="12" cy="12" r="9" />
                  <line x1="5.6" y1="5.6" x2="18.4" y2="18.4" />
                </svg>
                Not crawlable
              </span>
            )}
            <ReviewPill roaster={roaster} />
            <button
              type="button"
              onClick={startEditing}
              data-testid="roaster-edit"
              className="ml-auto shrink-0 rounded-md border border-wolisho-brown/[0.15] px-2.5 py-1 text-xs font-bold text-wolisho-brown"
            >
              Edit
            </button>
          </div>
        </div>
      ) : (
        <div className="mt-2 flex items-center gap-2">
          <span className="text-[13px] font-bold text-black/35">
            {roaster.shopUrl ? 'Platform not set' : 'No shop details yet'}
          </span>
          <button
            type="button"
            onClick={startEditing}
            data-testid="roaster-add-info"
            className="ml-auto inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-wolisho-red px-3.5 py-1.5 text-[13px] font-bold text-white"
          >
            Add info
          </button>
        </div>
      )}
    </div>
  );
}
